/**
 * /l/:slug — 公開ランディング（未認証アクセス可）
 */
import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';

import { Spinner } from '@/components/ui/spinner';
import { loadLandingSections, usePublicLanding } from '@/hooks/useLandings';
import { useLiffLineUid } from '@/hooks/useLiffLineUid';
import { usePublicPageMode } from '@/hooks/usePublicPageMode';
import { usePublicGoogleTagDefault } from '@/hooks/useSettings';
import { functions } from '@/lib/firebase';
import { resolveEffectiveTag, useGoogleTag } from '@/lib/googleTag';
import { getOrCreateVisitorId } from '@/lib/visitor';

import {
  SECTIONS,
  type SectionType,
  type VisualIdentity,
} from '@goenhub/landing-sections';
import { identityVars } from '@goenhub/landing-sections/identities';

import '@goenhub/landing-sections/styles.css';

import { enrichContentWithFallbackCta, getFallbackCtaHref } from '../utils/ctaFallback';

export function PublicLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: landing, isLoading } = usePublicLanding(slug);

  // body の overflow/height 制約を解除（公開ページ共通）
  usePublicPageMode();

  // Google タグ: LP 個別 (landing.googleTag) があれば優先、なければグローバル既定。
  const { data: googleTagDefault } = usePublicGoogleTagDefault();
  useGoogleTag(resolveEffectiveTag(landing?.googleTag, googleTagDefault));

  const { data: sections } = useQuery({
    queryKey: ['public_sections', landing?.id],
    enabled: !!landing?.id,
    queryFn: () => (landing?.id ? loadLandingSections(landing.id) : Promise.resolve([])),
  });

  useEffect(() => {
    if (landing) {
      document.title = `${landing.brand} — ${landing.title}`;
    }
  }, [landing]);

  // audience_events に landing_view を記録（一度だけ）
  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid');
  const lid = searchParams.get('lid');
  const src = searchParams.get('src'); // P1: 流入元タグ
  const ref = searchParams.get('ref'); // referral タグ（任意）
  const queryCampaignId = searchParams.get('campaignId');

  // LIFF: URL に既に lid があれば取得スキップ（短絡）。
  // 無い場合のみ LIFF 初期化を試み、LINE 内蔵ブラウザなら lineUid を補完する。
  const liff = useLiffLineUid(!!lid);
  const effectiveLid = lid || liff.lineUid;
  // LINE 内蔵ブラウザで lid がなかった場合は src も 'line' 扱いに昇格
  const effectiveSrc = src || (liff.isInClient ? 'line' : null);

  useEffect(() => {
    if (!landing?.id) return;
    if (!liff.ready) return; // LIFF 解決完了を待ってから 1 回だけ記録
    const visitorId = getOrCreateVisitorId();
    const fn = httpsCallable(functions, 'recordLandingView');
    fn({
      landingId: landing.id,
      cid: cid || null,
      lid: effectiveLid || null,
      src: effectiveSrc,
      ref: ref || null,
      visitorId,
      campaignId: queryCampaignId || landing.sourceCampaignId || null,
      metadata: {
        userAgent: navigator.userAgent.slice(0, 200),
        referrer: document.referrer.slice(0, 200) || null,
        liffEnriched: !lid && !!liff.lineUid,
      },
    }).catch(() => {
      // 計測失敗はユーザー体験に影響させない
    });
  }, [
    landing?.id,
    cid,
    effectiveLid,
    effectiveSrc,
    ref,
    queryCampaignId,
    landing?.sourceCampaignId,
    liff.ready,
    liff.lineUid,
  ]);

  const sortedRefs = useMemo(
    () =>
      (landing?.sections ?? [])
        .slice()
        // 非表示セクションは公開ページから完全に除外
        .filter((s) => !s.hidden)
        .sort((a, b) => a.order - b.order),
    [landing],
  );

  // 1:N 対応: フォールバック URL には campaignId を焼き込まない。
  // 下の enrichContentWithFallbackCta の opts.campaignId に一元化し、
  //   URL クエリ ?campaignId= > landing.sourceCampaignId の優先順で注入する。
  const fallbackCtaHref = useMemo(
    () => getFallbackCtaHref(landing?.assignedCampaigns),
    [landing?.assignedCampaigns],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (!landing) {
    return (
      <div className="flex items-center justify-center h-screen flex-col gap-2">
        <h1 className="text-2xl font-semibold">404 — Not Found</h1>
        <p className="text-sm text-gray-500">このページは存在しないか、非公開です。</p>
      </div>
    );
  }

  const identityCss = identityVars(landing.visualIdentity as VisualIdentity, landing.brandSettings);
  const brandContext = {
    logoUrl: landing.brandSettings?.logoStoragePath ?? null,
    socialLinks: landing.brandSettings?.socialLinks ?? null,
  };

  return (
    <div data-ls-identity={landing.visualIdentity} style={identityCss as React.CSSProperties}>
      {sortedRefs.map((ref) => {
        const sectionDoc = (sections ?? []).find((s) => s.id === ref.id);
        if (!sectionDoc) return null;
        const sectionDef = SECTIONS[ref.type as SectionType];
        if (!sectionDef) return null;
        const Comp = sectionDef.variants[ref.variant];
        if (!Comp) return null;
        const enriched = enrichContentWithFallbackCta(
          sectionDoc.content as Record<string, unknown>,
          fallbackCtaHref,
          {
            campaignId: queryCampaignId || landing.sourceCampaignId || null,
            cid: cid || null,
            lid: lid || null,
            landingId: landing.id,
          },
        );
        return (
          <div key={ref.id}>
            <Comp content={enriched as never} style={sectionDoc.style} brand={brandContext} />
          </div>
        );
      })}
    </div>
  );
}
